/**
 * WorkingGroup Page Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive working group detail page with advanced features:
 * - Individual working group information display
 * - Chair details with profile integration
 * - Terms of reference (ToR) display
 * - Meeting minutes with document access
 * - Team member profiles and organization
 * - Reference documents library
 * - Dynamic data fetching based on group ID
 * - Multi-section layout with dividers
 * 
 * Features:
 * - URL parameter-based group ID detection
 * - Parallel data fetching for multiple sources
 * - Chair-first team member sorting
 * - Filtered minutes by working group name
 * - Comprehensive group structure display
 * - Reference documents integration
 * - Back navigation functionality
 * - Responsive section layout
 * 
 * @returns {JSX.Element} WorkingGroup page component with comprehensive group details
 */
import React, { useEffect, useState } from 'react';
import Divider from '../components/Common/Divider';
import { useParams } from 'react-router-dom';
import apiHelper from '../utils/apiHelper';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import WGTor from '../components/WorkingGroups/WGTor';
import WGMinutes from '../components/WorkingGroups/WGMinutes';
import WGTeam from '../components/WorkingGroups/WGTeam';
import WGChairDetails from '../components/WorkingGroups/WGChairDetails';
import WGReferenceDocs from '../components/WorkingGroups/WGReferenceDocs';

import '../css/Colors.css';
import './WorkingGroup.css';

const { FILE_NAMES } = require('../utils/constants');

/**
 * WorkingGroup component for displaying individual working group details
 * with chair information, ToR, minutes, team, and reference documents
 */
function WorkingGroup() {
  const { id } = useParams();
  const [currentGroup, setCurrentGroup] = useState(null);
  const [torData, setTorData] = useState(null);
  const [minutesData, setMinutesData] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [referenceDocsData, setReferenceDocsData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [wgDataResult, torDataResult, minutesResult, teamResult, referenceDocsResult] = await Promise.all([
          apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_STRUCTURE),
          apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_TOR),
          apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_MINUTES),
          apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_MEMBER_DETAILS),
          apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_REFERENCE_DOCS)
        ]);

       

        const filteredData = wgDataResult.fileContent.filter(
          group => group.id === parseInt(id)
        );

     

        setCurrentGroup(filteredData[0]);
        setTorData(torDataResult.fileContent);

        // Filter minutes for this working group by name
        const groupMinutes = minutesResult.fileContent.filter(
          minute => minute.workingGroupName === filteredData[0].workingGroupName
        );
        setMinutesData(groupMinutes);

        // Filter team members for this working group
        const groupMembers = teamResult.fileContent.filter(
          member => member.workingGroupName === filteredData[0].workingGroupName
        ).sort((a, b) => {
          if (a.workingGroupPosition === 'Chair') return -1;
          if (b.workingGroupPosition === 'Chair') return 1;
          return a.lastName.localeCompare(b.lastName);
        });
        setTeamData(groupMembers);

        // Set reference docs data
        setReferenceDocsData(referenceDocsResult.fileContent);

      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    fetchData();
  }, [id]);


  if (!currentGroup || !torData || !minutesData || !teamData || !referenceDocsData) {
    return <LoadingSpinner />;
  }



  return (
    <div className='totalPage' id="wg-page">
      <section className="wrapper">
        <div className="container mt-6 pb-6">
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <h2 className="text-uppercase main-header wg-name" style={{ flex: '0 0 70%' }}>{currentGroup.workingGroupName}</h2> 
            <div style={{ flex: '0 0 30%', textAlign: 'right' }}>
              <button
                onClick={() => window.history.back()}
                className="btn btn-primary"
              >
                <i className="fas fa-arrow-left"></i> Back
              </button>
            </div>
          </div>
        </div>

        <div className="container">
          <WGChairDetails currentGroup={currentGroup} />
        </div>

        <Divider height={60} />

        <section className="wrapper bg-gradient-primary">
          <div className="container">
            <h2 className="text-uppercase main-header">Terms of Reference</h2>
          </div>
          <div className="container mt-10">
            <WGTor 
              currentGroup={currentGroup} 
              torData={torData}
            />
          </div>
        </section>


        <Divider height={60} />

        <section className="wrapper bg-gradient-primary">
          <div className="container">
            <h2 className="text-uppercase main-header">Minutes of Meetings</h2>
          </div>

          <div className="container mt-10">
            <WGMinutes data={minutesData} />
          </div>
        </section>


        <Divider height={60} />


        <section className="wrapper bg-gradient-primary">
          <div className="container pb-6">
            <h2 className="text-uppercase main-header">Meet the Team</h2>
          </div>
          <div className="container">
            <WGTeam data={teamData} />
          </div>
        </section>


        <Divider height={60} />


        <section className="wrapper bg-gradient-primary pb-12">
          <div className="container pb-6">
            <h2 className="text-uppercase main-header">Reference Documents</h2>
          </div>
          <div className="container">
            <WGReferenceDocs data={referenceDocsData} />
          </div>
        </section>

       
      </section>
    </div>
  );
}

export default WorkingGroup;